import { keepPreviousData, queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useFlag } from '@/common/hooks/feature-flags/useFlag';
import { isHttpError } from '@/common/utils/isHttpError';
import { fetchReportingStorePlannerDataSet } from '@/features/insights/api/plannerDataSet';
import type { ScenarioItemFilters } from '@/features/planner/api/adaptive-planner/scenarioItemFilters';
import { LoopPlannerTimelineItemsApi } from '@/features/planner/api/timelineItems';
import { plannerQueryKeys } from '@/features/planner/queryKeys';

interface TimelineItemsDataSetParams {
  loopId: number;
  scenarioId: number;
  filters?: ScenarioItemFilters;
  order?: string;
}

const REPORTING_STORE_MIRROR_LAG_RETRY_LIMIT = 15;
const REPORTING_STORE_MIRROR_LAG_RETRY_DELAY_MS = 3000;
const DEFAULT_RETRY_LIMIT = 3;
const DEFAULT_RETRY_DELAY_MS = 30000;

export const getTimelineItemsDataSetQueryOptions = ({
  loopId,
  scenarioId,
  filters,
  order,
  useReportingStore = false,
}: TimelineItemsDataSetParams & { useReportingStore?: boolean }) =>
  queryOptions({
    queryKey: [
      ...plannerQueryKeys.timelineItems.dataSet({ loopId, scenarioId, filters, order }),
      useReportingStore,
    ],
    queryFn: () =>
      useReportingStore
        ? fetchReportingStorePlannerDataSet({ loopId, scenarioId, filters, order })
        : LoopPlannerTimelineItemsApi.getDataSet({ loopId, scenarioId, filters, order }),
    ...(useReportingStore
      ? {
          retry: (failureCount: number, error: unknown) =>
            isHttpError(error, 404)
              ? failureCount < REPORTING_STORE_MIRROR_LAG_RETRY_LIMIT
              : failureCount < DEFAULT_RETRY_LIMIT,
          retryDelay: (attempt: number, error: unknown) =>
            isHttpError(error, 404)
              ? Math.min(1000 * 2 ** attempt, REPORTING_STORE_MIRROR_LAG_RETRY_DELAY_MS)
              : Math.min(1000 * 2 ** attempt, DEFAULT_RETRY_DELAY_MS),
        }
      : {}),
  });

export const useGetTimelineItemsDataSetSuspenseQuery = ({
  loopId,
  scenarioId,
  filters,
  order,
}: TimelineItemsDataSetParams) => {
  const useReportingStore = useFlag('reportingServicePlannerFilter');

  return useSuspenseQuery(
    getTimelineItemsDataSetQueryOptions({ loopId, scenarioId, filters, order, useReportingStore }),
  );
};

export const useGetTimelineItemsDataSetQuery = ({
  loopId,
  scenarioId,
  filters,
  order,
}: TimelineItemsDataSetParams) => {
  const useReportingStore = useFlag('reportingServicePlannerFilter');

  return useQuery({
    ...getTimelineItemsDataSetQueryOptions({
      loopId,
      scenarioId,
      filters,
      order,
      useReportingStore,
    }),
    placeholderData: keepPreviousData,
  });
};
